import { useState } from "react";
import { useLang } from "../i18n";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { Mail, LifeBuoy } from "lucide-react";

const SUPPORT_EMAIL = "info@streameyeq.com";

const paymentFaq = {
  en: [
    ["When is my money charged and released?", "Your payment is held in escrow the moment you support a streamer. It's only released to the streamer once the live stream is verified. Until then, your money is protected."],
    ["How do I request a refund?", "If a collaboration or debate didn't happen, email us at info@streameyeq.com with your account email and the streamer's name. Our team will review it and process the refund to your original payment method."],
    ["How long does a refund take?", "Once approved, refunds are sent back to your card immediately, but your bank may take 5–10 business days to show it."],
    ["Why was my account suspended?", "Accounts are automatically suspended after 3 refunds that count against the fan, to prevent abuse. If you think this was a mistake, contact us and an admin can review and reinstate your account."],
    ["Are my card details safe?", "Yes. All card data is handled exclusively by Stripe, our payment provider. Card numbers never reach or get stored on our servers."],
  ],
  es: [
    ["¿Cuándo se cobra y se libera mi dinero?", "Tu pago queda en custodia (escrow) en cuanto apoyas a un streamer. Solo se libera al streamer cuando se verifica el directo. Hasta entonces, tu dinero está protegido."],
    ["¿Cómo solicito un reembolso?", "Si una colaboración o debate no se realizó, escríbenos a info@streameyeq.com con el correo de tu cuenta y el nombre del streamer. Nuestro equipo lo revisará y procesará el reembolso a tu método de pago original."],
    ["¿Cuánto tarda un reembolso?", "Una vez aprobado, el reembolso se envía a tu tarjeta de inmediato, pero tu banco puede tardar de 5 a 10 días hábiles en reflejarlo."],
    ["¿Por qué se suspendió mi cuenta?", "Las cuentas se suspenden automáticamente tras 3 reembolsos que cuentan contra el fan, para evitar abusos. Si crees que fue un error, contáctanos y un administrador puede revisar y reactivar tu cuenta."],
    ["¿Están seguros los datos de mi tarjeta?", "Sí. Todos los datos de la tarjeta los gestiona exclusivamente Stripe, nuestro proveedor de pagos. Los números de tarjeta nunca llegan ni se guardan en nuestros servidores."],
  ],
};

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", topic: "refund", message: "" });
  const [sending, setSending] = useState(false);
  const faq = paymentFaq[lang] || paymentFaq.en;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact_ok"));
      setForm({ name: "", email: "", topic: "refund", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact_err"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-5xl mx-auto px-6 py-16">
      <div className="flex items-center gap-3 mb-2">
        <LifeBuoy className="text-emerald-400" size={26} />
        <h1 className="text-4xl font-black tracking-tighter text-white">{t("contact_title")}</h1>
      </div>
      <p className="text-sm text-zinc-400 max-w-2xl mb-10">{t("contact_subtitle")}</p>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Support email + form */}
        <div className="lg:col-span-3 space-y-6">
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-email-card"
            className="flex items-center gap-4 bg-zinc-900 border border-zinc-800 rounded-2xl p-5 hover:border-emerald-800/60 transition-colors group">
            <span className="h-11 w-11 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
              <Mail size={20} className="text-emerald-400" />
            </span>
            <div>
              <p className="text-xs text-zinc-500">{t("contact_email_label")}</p>
              <p className="text-white font-semibold group-hover:text-emerald-400 transition-colors">{SUPPORT_EMAIL}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{t("contact_email_note")}</p>
            </div>
          </a>

          <form onSubmit={submit} data-testid="contact-form" className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-400 mb-1">{t("contact_name")}</label>
                <Input data-testid="contact-name" required value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-400 mb-1">{t("contact_email")}</label>
                <Input data-testid="contact-email-input" required type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-400 mb-1">{t("contact_topic")}</label>
              <select data-testid="contact-topic" value={form.topic} onChange={(e) => set("topic", e.target.value)}
                className="w-full rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm px-3 py-2 focus:outline-none focus:border-emerald-700">
                <option value="refund">{t("contact_topic_refund")}</option>
                <option value="payment">{t("contact_topic_payment")}</option>
                <option value="general">{t("contact_topic_general")}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-400 mb-1">{t("contact_message")}</label>
              <Textarea data-testid="contact-message" required rows={5} value={form.message}
                onChange={(e) => set("message", e.target.value)}
                className="bg-zinc-950 border-zinc-800 text-white resize-none" />
            </div>
            <Button data-testid="contact-submit" type="submit" disabled={sending}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold">
              {sending ? t("contact_sending") : t("contact_submit")}
            </Button>
          </form>
        </div>

        {/* Payment FAQs */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-white mb-3">{t("contact_faq_title")}</h2>
          <Accordion type="single" collapsible data-testid="contact-faq" className="bg-zinc-900 border border-zinc-800 rounded-2xl px-4">
            {faq.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`} className="border-zinc-800">
                <AccordionTrigger data-testid={`contact-faq-q-${i}`} className="text-sm text-white text-left hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-400 leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
